import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { AppState } from '../../app.state';
import { MediaPlayerState } from '../../enum/media-player/media-player-state';
import { Route } from '../../enum/route';
import { MediaRadio } from '../../objects/media-player/media-radio';
import {
  muteAudioVolume,
  pauseMedia,
  resumeMedia,
  unmuteAudioVolume,
  updateAudioVolume,
} from '../../player.actions';
import { LayoutService } from '../../services/layout.service';
import { MediaPlayerService } from '../../services/media-player/media-player.service';
import { NowPlayingService } from '../../services/now-playing.service';
import { MiscUtils } from '../../util/misc.utils';
import { AudioWaveformComponent } from '../audio-waveform/audiowaveform.component';

/**
 * Full-width transport bar pinned to the bottom of the shell — the standard
 * music-player placement. Collapses to a mini bar on compact viewports.
 *
 * Everything here is derived from signals so the OnPush view actually
 * refreshes when the player state changes.
 */
@Component({
  selector: 'player-bar-component',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatSliderModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    AudioWaveformComponent,
  ],
  templateUrl: './player-bar.component.html',
  styleUrl: './player-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayerBarComponent {
  protected readonly MiscUtils = MiscUtils;
  protected readonly layout = inject(LayoutService);

  private readonly nowPlaying = inject(NowPlayingService);
  private readonly store = inject(Store<AppState>);
  private readonly router = inject(Router);

  private readonly media = this.nowPlaying.state;

  protected readonly imageFailed = signal(false);
  /** Set while the user drags, so the slider doesn't fight the store. */
  private readonly localVolume = signal<number | null>(null);

  protected readonly hasTrack = computed(() => !!this.media()?.mediaContent);
  protected readonly isPlaying = this.nowPlaying.isPlaying;
  protected readonly isBusy = this.nowPlaying.isLoading;
  /** Such stations can't feed the waveform, so it stays hidden for them. */
  protected readonly equalizerBypassed = inject(MediaPlayerService).equalizerBypassed;
  protected readonly isError = computed(
    () => this.media()?.playerState === MediaPlayerState.ERROR,
  );

  protected readonly title = computed(
    () => this.media()?.mediaContent?.name ?? 'Nothing playing',
  );

  protected readonly subtitle = computed(() => {
    const m = this.media();
    const radio = m?.mediaContent as MediaRadio | undefined;
    return m?.currentStreamTitle || radio?.country || null;
  });

  protected readonly imageUrl = computed(() => {
    const radio = this.media()?.mediaContent as MediaRadio | undefined;
    return radio?.iconUrl || null;
  });

  protected readonly elapsed = computed(() =>
    this.formatDuration(this.media()?.progress ?? null),
  );

  protected readonly volume = computed(
    () => this.localVolume() ?? this.media()?.volume ?? 0.15,
  );
  protected readonly isMuted = computed(() => this.volume() <= 0);

  protected playPause(): void {
    if (this.isPlaying()) {
      this.store.dispatch(pauseMedia());
      return;
    }
    if (this.hasTrack()) {
      this.store.dispatch(resumeMedia());
      return;
    }
    void this.nowPlaying.playRandom();
  }

  protected onVolume(value: number): void {
    this.localVolume.set(value);
    this.store.dispatch(updateAudioVolume({ newAudioVolume: value }));
  }

  protected muteUnmute(): void {
    const muted = this.isMuted();
    this.localVolume.set(null);
    this.store.dispatch(muted ? unmuteAudioVolume() : muteAudioVolume());
  }

  protected openNowPlaying(): void {
    this.router.navigate([Route.NOW_PLAYING]);
  }

  protected onImageError(): void {
    this.imageFailed.set(true);
  }

  private formatDuration(seconds: number | null): string {
    if (seconds == null) {
      return '00:00';
    }
    const iso = new Date(seconds * 1000).toISOString();
    return seconds >= 3600 ? iso.substring(11, 19) : iso.substring(14, 19);
  }
}
